import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AuthContext } from "../../../../context/authcontext";
import { useEmailProviders } from "../../../../hooks/useEmailProviders";
import { useEmailContext } from "../../../../context/emailContext";
import { all_routes } from "../../../router/all_routes";
import AnimatedCircleButton from "../../../../components/ui/AnimatedCircleButton";
import IconTabList, { IconBadge, TabButton } from "../../../../components/ui/IconTabList";
import { MailAuthFolder } from "./mailAuthFolder.service";

export type TabKey = "inbox" | "important" | "sent" | "draft" | "spam" | "trash";

interface EmailTabsProps {
  activeTab: TabKey | null;
  activeFolderId?: number;
  onComposeClick: () => void;
  onTabClick?: (tab: TabKey) => void;
}

interface TabDefinition {
  key: TabKey;
  label: string;
  icon: string;
}

const TAB_ITEMS: TabDefinition[] = [
  { key: "inbox", label: "Inbox", icon: "fas fa-inbox" },
  { key: "important", label: "Important", icon: "far fa-star" },
  { key: "sent", label: "Sent Mail", icon: "far fa-paper-plane" },
  { key: "draft", label: "Drafts", icon: "far fa-file-alt" },
  { key: "spam", label: "Spam", icon: "fas fa-ban" },
  { key: "trash", label: "Trash", icon: "far fa-trash-alt" },
];

const Sidebar = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 22px 16px 20px;
  margin-bottom: 24px;
  background: #ffffff;
  border: 1px solid #f2ecff;
  box-shadow: 0 20px 45px -28px rgba(130, 78, 235, 0.35);

  &::after {
    content: "";
    position: absolute;
    width: 160px;
    height: 160px;
    bottom: -90px;
    left: -60px;
    background: radial-gradient(circle, rgba(255, 162, 1, 0.1), transparent 70%);
    filter: blur(14px);
    pointer-events: none;
  }
`;

const CountBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: linear-gradient(135deg, #4a46f2ff, #8886deff);
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 800;
`;

const Divider = styled.div`
  height: 1px;
  margin: 10px 4px 12px;
  background: linear-gradient(90deg, transparent, rgba(130, 78, 235, 0.18), transparent);
`;

const Chevron = styled.i<{ $open: boolean }>`
  margin-left: auto;
  font-size: 12px;
  color: rgba(130, 78, 235, 0.5);
  transition: transform 0.25s ease;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
`;

const FolderPanel = styled.div<{ $open: boolean }>`
  display: grid;
  overflow: hidden;
  grid-template-rows: ${(p) => (p.$open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.35s ease;
`;

const FolderList = styled.div`
  min-height: 0;
  max-height: 250px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 4px 6px 46px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(130, 78, 235, 0.2);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(130, 78, 235, 0.4);
  }
`;

const FolderRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  color: ${(p) => (p.$active ? "#824eeb" : "#6b6f7b")};
  background: ${(p) => (p.$active ? "rgba(130, 78, 235, 0.1)" : "transparent")};
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  .folder-icon {
    flex-shrink: 0;
    font-size: 12px;
    color: ${(p) => (p.$active ? "#824eeb" : "#6b6f7b")};
  }

  .folder-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .folder-action {
    flex-shrink: 0;
    padding: 4px;
    font-size: 12px;
    color: rgba(130, 78, 235, 0.55);
    opacity: 0;
    transition: opacity 0.2s ease, color 0.2s ease;

    &:hover {
      color: #824eeb;
    }

    @media (hover: none) {
      opacity: 0.55;
    }
  }

  &:hover .folder-action,
  &:focus-within .folder-action {
    opacity: 1;
  }

  &:hover {
    background: ${(p) => (p.$active ? "rgba(130, 78, 235, 0.14)" : "rgba(130, 78, 235, 0.06)")};
    color: ${(p) => (p.$active ? "#824eeb" : "#1c1c30")};
  }
`;

const FolderNameInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px solid rgba(130, 78, 235, 0.4);
  border-radius: 6px;
  padding: 3px 6px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  color: #1c1c30;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #824eeb;
    box-shadow: 0 0 0 2px rgba(130, 78, 235, 0.15);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const FolderMeta = styled.div`
  padding: 8px 10px;
  color: #9a9ea8;
  font-size: 12.5px;
  font-style: italic;
`;

const EmailTabs: React.FC<EmailTabsProps> = ({ activeTab, activeFolderId, onComposeClick, onTabClick }) => {
  const navigate = useNavigate();
  const { unreadCount } = useContext(AuthContext);
  const { hasConnectedProviders, providers } = useEmailProviders();
  const {
    mailFolders: folders,
    mailFoldersLoading: foldersLoading,
    mailFoldersOpen: foldersOpen,
    setMailFoldersOpen: setFoldersOpen,
    renamingMailFolderId: renamingId,
    renameMailFolder: renameFolder,
    ensureMailFoldersLoaded,
    loadMoreMailFolders,
    hasMoreMailFolders,
    loadingMoreMailFolders,
  } = useEmailContext();
  const [editingFolderId, setEditingFolderId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
      if (hasMoreMailFolders && !loadingMoreMailFolders) {
        loadMoreMailFolders();
      }
    }
  };

  useEffect(() => {
    ensureMailFoldersLoaded();
  }, [ensureMailFoldersLoaded]);

  useEffect(() => {
    if (activeFolderId) setFoldersOpen(true);
  }, [activeFolderId]);

  useEffect(() => {
    if (editingFolderId !== null) {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }
  }, [editingFolderId]);

  const getBuiltinCount = (key: string) => {
    let count = 0;
    if (providers && providers.length > 0) {
      providers.forEach(provider => {
        const builtin = provider.email_builtin_folder_stats;
        if (builtin) {
          if (key === "inbox") count += builtin.inbox || 0;
          if (key === "important") count += builtin.important || 0;
          if (key === "sent") count += builtin.sentMail || 0;
          if (key === "draft") count += builtin.drafts || 0;
          if (key === "spam") count += builtin.spam || 0;
          if (key === "trash") count += builtin.trash || 0;
        }
      });
    }
    return count;
  };

  const getCustomFolderCount = (folderName: string) => {
    let count = 0;
    if (providers && providers.length > 0) {
      providers.forEach(provider => {
        const custom = provider.email_custom_folder_stats;
        if (custom && custom[folderName]) {
          count += custom[folderName];
        }
      });
    }
    return count;
  };

  const tabItems = TAB_ITEMS.map((tab) => {
    let count = getBuiltinCount(tab.key);
    if (tab.key === "inbox" && unreadCount > 0 && hasConnectedProviders) {
      count = unreadCount;
    }

    if (tab.key === "important" || tab.key === "spam") {
      count = 0;
    }

    return {
      ...tab,
      badge: count > 0 ? <CountBadge>{count}</CountBadge> : undefined,
    };
  });

  const handleTabSelect = (key: string) => {
    if (hasConnectedProviders && onTabClick) onTabClick(key as TabKey);
  };

  const handleFolderClick = (folder: MailAuthFolder) => {
    if (editingFolderId === folder.mail_auth_folder_id) return;
    navigate(all_routes.emailFolder.replace(":folderId", folder.mail_auth_folder_id.toString()));
  };

  const startEditing = (e: React.SyntheticEvent, folder: MailAuthFolder) => {
    e.stopPropagation();
    setEditingFolderId(folder.mail_auth_folder_id);
    setEditValue(folder.folder_display_name);
  };

  const cancelEditing = () => {
    setEditingFolderId(null);
    setEditValue("");
  };

  const commitEditing = async (folder: MailAuthFolder) => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === folder.folder_display_name) {
      cancelEditing();
      return;
    }
    cancelEditing();
    try {
      await renameFolder(folder.mail_auth_folder_id, trimmed);
    } catch {
    }
  };

  return (
    <div className="col-lg-3 col-md-12">
      <Sidebar>
        <div style={{ marginBottom: 22 }}>
          <AnimatedCircleButton
            icon="fas fa-pen"
            label="Compose"
            disabled={!hasConnectedProviders}
            onClick={hasConnectedProviders ? onComposeClick : undefined}
            title={!hasConnectedProviders ? "Connect an email provider to compose" : undefined}
          />
        </div>

        <IconTabList
          items={tabItems}
          activeKey={activeTab ?? undefined}
          onSelect={handleTabSelect}
          disabled={!hasConnectedProviders}
        />

        <Divider />

        <TabButton type="button" $active={!!activeFolderId} onClick={() => setFoldersOpen(!foldersOpen)}>
          <IconBadge $active={!!activeFolderId}>
            <i className="fas fa-folder" />
          </IconBadge>
          <span>Folders</span>
          <Chevron className="fas fa-chevron-down" $open={foldersOpen} />
        </TabButton>

        <FolderPanel $open={foldersOpen}>
          <FolderList onScroll={handleScroll}>
            {foldersLoading ? (
              <FolderMeta>Loading folders...</FolderMeta>
            ) : folders.length === 0 ? (
              <FolderMeta>No folders yet</FolderMeta>
            ) : (
              <>
                {folders.map((folder) => {
                  const isEditing = editingFolderId === folder.mail_auth_folder_id;
                  const isSaving = renamingId === folder.mail_auth_folder_id;
                  return (
                    <FolderRow
                      key={folder.mail_auth_folder_id}
                      $active={activeFolderId === folder.mail_auth_folder_id}
                      onMouseDown={(e) => {
                        if (isEditing) e.preventDefault();
                      }}
                      onClick={() => handleFolderClick(folder)}
                    >
                      <i className="fas fa-folder folder-icon" />
                      {isEditing ? (
                        <FolderNameInput
                          ref={editInputRef}
                          value={editValue}
                          disabled={isSaving}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelEditing();
                            }
                          }}
                          onBlur={() => commitEditing(folder)}
                        />
                      ) : (
                        <span className="folder-name" title={folder.folder_display_name}>
                          {folder.folder_display_name}
                        </span>
                      )}
                      {(() => {
                        const count = getCustomFolderCount(folder.folder_display_name);
                        return count > 0 && !isEditing ? (
                          <CountBadge style={{ marginLeft: "auto", marginRight: isSaving ? "4px" : "8px" }}>
                            {count}
                          </CountBadge>
                        ) : null;
                      })()}
                      {isSaving ? (
                        <i className="fas fa-spinner fa-spin folder-action" style={{ opacity: 1 }} />
                      ) : !isEditing ? (
                        <i
                          className="fas fa-pen folder-action"
                          title="Rename folder"
                          role="button"
                          tabIndex={0}
                          aria-label={`Rename ${folder.folder_display_name}`}
                          onClick={(e) => startEditing(e, folder)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              startEditing(e, folder);
                            }
                          }}
                        />
                      ) : null}
                    </FolderRow>
                  );
                })}
                {loadingMoreMailFolders && (
                  <FolderMeta style={{ textAlign: "center", marginTop: 8 }}>
                    <i className="fas fa-spinner fa-spin" /> Loading more...
                  </FolderMeta>
                )}
              </>
            )}
          </FolderList>
        </FolderPanel>
      </Sidebar>
    </div>
  );
};

export default EmailTabs;
